import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CasinoPromoCard from "../../components/CasinoPromoCard";
import { classicGames } from "../../lib/classicGames";
import {
  Bars3BottomLeftIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  StarIcon,
  TvIcon,
} from "@heroicons/react/24/outline";

const lobbyTabs = [
  "Lobby",
  "Originals",
  "Hot Games",
  "Slots",
  "Live Casino",
  "Game Shows",
  "Table Games",
  "Blackjack",
  "Roulette",
  "Baccarat",
];

const liveCasinoCategories = [
  "Live Casino",
  "Roulette",
  "Baccarat",
  "Blackjack",
  "Game Shows",
  "Dragon Tiger",
  "Sic Bo",
  "VIP Tables",
];

const liveCasinoCards = [
  { title: "Microgaming Live", provider: "Evolution", players: 39, image: "/img/bonus.jpeg", tone: "from-[#b96f46]/10 to-transparent" },
  { title: "Roulette", provider: "WM", players: 93, image: "/img/win-bt.jpeg", tone: "from-[#d5b16a]/10 to-transparent" },
  { title: "Sic Bo", provider: "WM", players: 93, image: "/img/spin-win.jpeg", tone: "from-[#8764e8]/10 to-transparent" },
  { title: "Red White", provider: "WM", players: 100, image: "/img/crash.png", tone: "from-[#ce4d50]/10 to-transparent" },
  { title: "Dragon Phoenix", provider: "WM", players: 34, image: "/img/bonus.jpeg", tone: "from-[#bf5942]/10 to-transparent" },
  { title: "Dragon Tiger", provider: "WM", players: 24, image: "/img/win-bt.jpeg", tone: "from-[#6178d3]/10 to-transparent" },
  { title: "Live Baccarat", provider: "WM", players: 35, image: "/img/spin-win.jpeg", tone: "from-[#44c58f]/10 to-transparent" },
  { title: "Niu Niu", provider: "WM", players: 19, image: "/img/coin.png", tone: "from-[#7fa2d9]/10 to-transparent" },
  { title: "VIP Roulette", provider: "Evolution", players: 18, image: "/img/bonus.jpeg", tone: "from-[#c28c56]/10 to-transparent" },
  { title: "Infinite Blackjack", provider: "Evolution", players: 72, image: "/img/win-bt.jpeg", tone: "from-[#5482b7]/10 to-transparent" },
  { title: "Lightning Dice", provider: "Evolution", players: 41, image: "/img/spin-win.jpeg", tone: "from-[#a861d8]/10 to-transparent" },
  { title: "Salon Privé", provider: "WM", players: 12, image: "/img/coin.png", tone: "from-[#7fb680]/10 to-transparent" },
];

function LiveCard({ card }) {
  return (
    <button
      type="button"
      className="group overflow-hidden rounded-[18px] bg-[#1b2027] text-left transition hover:-translate-y-1 hover:bg-[#212630]"
    >
      <div className="relative aspect-[0.74] w-full overflow-hidden">
        <img
          src={card.image}
          alt={card.title}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${card.tone}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
        <div className="absolute bottom-2 left-2 right-2">
          <div className="text-2xl font-black leading-none text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]">
            {card.title}
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
            <span>{card.provider}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-black/30 px-2 py-1">
              <TvIcon className="h-3.5 w-3.5" />
              {card.players}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function ClassicCard({ card, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group overflow-hidden rounded-[18px] bg-[#1b2027] text-left transition hover:-translate-y-1 hover:bg-[#212630]"
    >
      <div className="relative h-32 overflow-hidden">
        <img
          src={card.image}
          alt={card.title}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <div className="text-lg font-black text-white">{card.title}</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
            {card.subtitle}
          </div>
        </div>
      </div>
      <div className="px-3 py-3">
        <div className="text-sm leading-6 text-slate-300">{card.copy}</div>
      </div>
    </button>
  );
}

export default function InHouseGamesPage() {
  const [activeTab, setActiveTab] = useState("Live Casino");
  const navigate = useNavigate();

  const cards = useMemo(() => liveCasinoCards, []);

  return (
    <div className="min-h-screen bg-[#171b22] text-white md:mt-16">
      <div className="mx-auto max-w-[1540px] px-3 py-4 sm:px-4 lg:px-5">
        <div className="grid gap-3 xl:grid-cols-[1fr]">
          <CasinoPromoCard ctaTo="/register" />

          <section className="rounded-[18px] bg-[#1f242d] px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.2)]">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <input
                readOnly
                value=""
                placeholder="Search games"
                className="w-full rounded-[14px] border border-[#2a303a] bg-[#2a2f31] py-3 pl-12 pr-4 text-sm text-white placeholder:text-slate-500"
              />
            </div>
          </section>

          <section className="rounded-[18px] bg-[#1f242d] px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.2)]">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {lobbyTabs.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-[12px] px-4 py-3 text-sm font-semibold transition ${
                      active
                        ? "bg-[#353b45] text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {tab === "Lobby" ? <Bars3BottomLeftIcon className="h-4 w-4" /> : null}
                    {tab === "Live Casino" ? <PlayIcon className="h-4 w-4" /> : null}
                    {tab === "Originals" ? <StarIcon className="h-4 w-4" /> : null}
                    {tab}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="rounded-[22px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.24)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-2xl font-black text-white">Live Casino</div>
              <div className="flex items-center gap-2">
                <button className="rounded-[12px] bg-[#353b45] px-3 py-2 text-xs font-bold text-white">
                  All
                </button>
                <button className="rounded-[12px] bg-[#2a3039] px-3 py-2 text-xs font-bold text-slate-400">
                  {"<"}
                </button>
                <button className="rounded-[12px] bg-[#2a3039] px-3 py-2 text-xs font-bold text-slate-400">
                  {">"}
                </button>
              </div>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
              {liveCasinoCategories.map((category) => {
                const active = category === "Live Casino";
                return (
                  <button
                    key={category}
                    type="button"
                    className={`whitespace-nowrap rounded-[12px] px-4 py-2 text-sm font-semibold transition ${
                      active
                        ? "bg-[#353b45] text-white"
                        : "bg-[#252b34] text-slate-400 hover:text-white"
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {cards.map((card) => (
                <LiveCard key={`${card.title}-${card.provider}`} card={card} />
              ))}
            </div>
          </section>

          <section className="rounded-[22px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.24)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-2xl font-black text-white">Classic Games</div>
                <div className="mt-1 text-sm text-slate-400">
                  Skill-based rooms and board games back in the main casino flow.
                </div>
              </div>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {classicGames.map((game) => (
                <ClassicCard
                  key={game.to}
                  card={game}
                  onOpen={() => navigate(game.to)}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
